'use client';

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useState,
} from 'react';
import Cookie from 'js-cookie';
import { ConsentCode, ConsentContextValue, ConsentState } from 'types/consent';
import { isCA, isEU } from '../utils';

const STORAGE_KEY = 'mx_consent_v1';

const DEFAULT_STORED: ConsentState = {
  ads: false,
  analytics: false,
  timestamp: 0,
};

function readStoredConsent(): ConsentState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : DEFAULT_STORED;
  } catch {
    return DEFAULT_STORED;
  }
}

function readServerConsentCode(): ConsentCode {
  try {
    const match = Cookie.get('mx-consent');
    if (!match) return ConsentCode.UNKNOWN;
    return match as ConsentCode;
  } catch {
    return ConsentCode.UNKNOWN;
  }
}

const ConsentContext = createContext<ConsentContextValue | null>(null);

export function ConsentProvider({ children }: { children: React.ReactNode }) {
  const [stored, setStored] = useState<ConsentState>(readStoredConsent);

  const serverCode = useMemo(() => readServerConsentCode(), []);

  const resolvedCode = useMemo(() => {
    if (serverCode !== ConsentCode.UNKNOWN) return serverCode;
    return isEU()
      ? ConsentCode.BOTH_REQUIRED
      : isCA()
      ? ConsentCode.ADS_REQUIRED
      : ConsentCode.NOT_REQUIRED;
  }, [serverCode]);

  const requiresConsent =
    resolvedCode === ConsentCode.ADS_REQUIRED ||
    resolvedCode === ConsentCode.BOTH_REQUIRED;

  const requiresAnalyticsConsent = resolvedCode === ConsentCode.BOTH_REQUIRED;

  const hasUserChosen = stored.timestamp > 0;

  const allowAds = !requiresConsent || stored.ads === true;

  const allowAnalytics = !requiresAnalyticsConsent || stored.analytics === true;

  const save = useCallback((next: ConsentState) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    setStored(next);
  }, []);

  const acceptNecessaryOnly = useCallback(() => {
    const analytics = requiresAnalyticsConsent ? false : undefined;
    save({ ads: false, analytics, timestamp: Date.now() });
  }, [requiresAnalyticsConsent, save]);

  const acceptAll = useCallback(() => {
    save({ ads: true, analytics: true, timestamp: Date.now() });
  }, [save]);

  const value = useMemo(
    () => ({
      resolvedCode,
      hasUserChosen,
      requiresConsent,
      allowAds,
      allowAnalytics,
      acceptNecessaryOnly,
      acceptAll,
    }),
    [
      resolvedCode,
      hasUserChosen,
      requiresConsent,
      allowAds,
      allowAnalytics,
      acceptNecessaryOnly,
      acceptAll,
    ]
  );

  return (
    <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
  );
}

export function useConsent() {
  const ctx = useContext(ConsentContext);
  if (!ctx) throw new Error('useConsent must be used within ConsentProvider');
  return ctx;
}
